import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { OneClickPaymentResponse } from 'api/PaymentApi';
import { VIP_RESUBSCRIBE_SESSION_STORAGE_KEY } from 'helpers/constants';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { usePromoteVipPopupAnalytics } from 'hooks/analytics/usePromoteVipPopupAnalytics';
import { useVipPayMutation } from 'hooks/mutations/useVipPayMutation';
import { useVipResignMutation } from 'hooks/mutations/useVipResignMutation';
import { useOneClickPayment } from 'hooks/useOneClickPayment';
import { init } from 'store/common/thunks';
import { setOneClickPaymentVerifyUrl } from 'store/oneClickPayment/oneClickPaymentSlice';
import {
  getOneClickPaymentOrderId,
  getOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/selectors';
import { setVipPaymentSuccess } from 'store/payment/paymentSlice';
import {
  getVipDiscountAvailable,
  getVipPackages,
} from 'store/payment/selectors';

import { useToast } from './useToast';

const FREE_VIP_REDIRECT_DELAY = 1000;

interface Props {
  onClose?: () => void;
}

export const usePromoteVipPopupPayment = ({ onClose }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const timer = useRef<NodeJS.Timeout | null>(null);

  const vipPackages = useSelector(getVipPackages);
  const orderId = useSelector(getOneClickPaymentOrderId);
  const verifyUrl = useSelector(getOneClickPaymentVerifyUrl);
  const isVipDiscountAvailable = useSelector(getVipDiscountAvailable);

  const isShowTracked = useRef(false);

  const vipPayMutation = useVipPayMutation();
  const vipResignMutation = useVipResignMutation();

  const { cardMask, isProcess, handleRecurrentSubmit } = useOneClickPayment({
    recurrentMutation: vipPayMutation,
    resignMutation: vipResignMutation,
    isEnabledResubscription: true,
  });

  const { showErrorToast } = useToast();

  const {
    trackPromoteVipPopupShow,
    trackPromoteVipPopupClick,
    trackPromoteVipPopupClose,
  } = usePromoteVipPopupAnalytics();

  const vipPackage = vipPackages[0];

  const handleSuccessPayment = (data: OneClickPaymentResponse) => {
    trackPromoteVipPopupClick({
      isDiscount: !isVipDiscountAvailable,
      orderId: data.order.id,
    });
    dispatch(setVipPaymentSuccess());
    dispatch(init());
    onClose && onClose();
  };

  const handleFailPayment = (data: OneClickPaymentResponse) => {
    trackPromoteVipPopupClick({
      isDiscount: !isVipDiscountAvailable,
      orderId: data.order.id,
    });
    dispatch(setVipPaymentSuccess());
    dispatch(init());
    onClose && onClose();
  };

  const handleResubscribePayment = () => {
    showErrorToast({
      title: 'Oops, something went wrong. Try Another Card',
    });

    setSessionStorageItem(VIP_RESUBSCRIBE_SESSION_STORAGE_KEY, '1');

    timer.current = setTimeout(() => {
      history.push(`/payments_vip/${vipPackage.id}`);
      onClose && onClose();
    }, FREE_VIP_REDIRECT_DELAY);
  };

  const handleVerifyDeclinePayment = (data: OneClickPaymentResponse) => {
    trackPromoteVipPopupClick({
      isDiscount: !isVipDiscountAvailable,
      orderId: data.order.id,
    });
    dispatch(setOneClickPaymentVerifyUrl(data.verify_url));
  };

  const handlePay = () => {
    if (getSessionStorageItem(VIP_RESUBSCRIBE_SESSION_STORAGE_KEY)) {
      history.push(`/payments_vip/${vipPackage.id}`);

      trackPromoteVipPopupClick({
        isDiscount: !isVipDiscountAvailable,
      });

      onClose && onClose();

      return;
    }

    handleRecurrentSubmit({
      packageId: vipPackage.id,
      onSuccessPayment: handleSuccessPayment,
      on3dsVerifyPaymentFail: handleVerifyDeclinePayment,
      onInsufficientFundsPaymentFail: handleFailPayment,
      onFinalPaymentFail: handleFailPayment,
      onResubscribePaymentFail: handleResubscribePayment,
    });
  };

  const handleClose = () => {
    trackPromoteVipPopupClose({
      isDiscount: !isVipDiscountAvailable,
    });
    onClose && onClose();
  };

  useEffect(() => {
    if (!isShowTracked.current) {
      trackPromoteVipPopupShow({ isDiscount: !isVipDiscountAvailable });
      isShowTracked.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVipDiscountAvailable]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return {
    vipPackage,
    cardMask,
    verifyUrl,
    orderId,
    isSubmitting: isProcess,
    isVipDiscountAvailable,
    handlePay,
    handleClose,
  };
};
